import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { SelectionRaceCard } from 'src/domains/sportsbook/webview/components/selectionRaceCard/SelectionRaceCard';
import { SelectionCast } from 'src/domains/sportsbook/webview/components/selectionCast/SelectionCast';
import { SelectionCastByPlace } from 'src/domains/sportsbook/webview/components/selectionCast/SelectionCastByPlace';
import {
    NonRunnerCell,
    RaceRunnerInfo,
    RaceRunnerNameAndPlace,
    RunnerDescription,
    RunnerDetails,
    SpanSelection,
} from 'src/domains/sportsbook/webview/components/raceSummary/utils/Utils.style';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';

export const getPlace = (selection: SelectionModel): React.ReactNode => {
    const number = selection.metaDataNumber;
    const drawn = selection.metaDataDrawn ?? null;

    return (
        <>
            {number ?? 'n/a'}
            <br />
            {drawn === null ? null : `(${drawn})`}
        </>
    );
};

export function getRunner(
    selection: SelectionModel,
    isUnnamedFavourite: boolean,
    viewDescription: boolean,
    displayTrainer?: boolean,
    isCast?: boolean,
    isSpecials?: boolean
): React.ReactNode {
    const jockey = selection.metaDataJockey ?? null;
    const trainer = selection.metaDataTrainer ?? null;

    const description = jockey === null ? trainer ?? '' : trainer === null ? jockey : `${jockey} / ${trainer}`;

    return selection.name === '' ? null : (
        <RaceRunnerInfo>
            <RaceRunnerNameAndPlace
                isCast={isCast ?? false}
                data-test='runner-name'
            >
                {selection.name}
            </RaceRunnerNameAndPlace>

            {isUnnamedFavourite || !viewDescription || displayTrainer !== true || isSpecials === true ? null : (
                <RunnerDescription data-test='runner-description'>{description}</RunnerDescription>
            )}
        </RaceRunnerInfo>
    );
}

export const getHorseRacingRunner = (
    selection: SelectionModel,
    isUnnamedFavourite: boolean,
    isCast?: boolean,
    isSpecials?: boolean
): React.ReactNode => {
    const jockey = selection.metaDataJockey ?? '';
    const trainer = selection.metaDataTrainer ?? '';

    const trainerNextToJockey = ` | ${trainer}`;

    const displayJockeyAndTrainer = (): string | null => {
        if (jockey === '' && trainer === '') {
            return null;
        }
        if (jockey === '') {
            return trainer;
        }
        if (trainer === '') {
            return jockey;
        }
        return `${jockey}${trainerNextToJockey}`;
    };

    const number = selection.metaDataNumber ?? null;
    const numberToDisplay = number === null || number === '' || number === '0' ? null : `${number} `;
    const drawn = selection.metaDataDrawn ?? null;
    const drawnToDisplay = drawn === null || drawn === '' ? null : `(${drawn}) `;

    return selection.name === '' ? null : (
        <RaceRunnerInfo>
            <RaceRunnerNameAndPlace
                data-test='runner-name'
                isCast={isCast ?? false}
            >
                {numberToDisplay}
                {selection.name}
            </RaceRunnerNameAndPlace>

            {isUnnamedFavourite || isSpecials === true ? undefined : (
                <RunnerDetails data-test='runner-description'>
                    {drawnToDisplay}
                    {displayJockeyAndTrainer()}
                </RunnerDetails>
            )}
        </RaceRunnerInfo>
    );
};

export interface NotRunningPriceViewType {
    extraPlaces?: boolean;
    variant: DefaultComponentVariantType | null;
}

export const NotRunningPriceView = observer('NotRunningPriceView', (props: NotRunningPriceViewType): JSX.Element => {
    return (
        <NonRunnerCell
            extraPlaces={props.extraPlaces}
            variant={props.variant}
        >
            NR
        </NonRunnerCell>
    );
});

export const NotVisiblePriceView = observer('NotVisiblePriceView', (): JSX.Element => {
    return (
        <SpanSelection>
            <span>-</span>
        </SpanSelection>
    );
});

export const getWinner = (
    selection: SelectionModel,
    variant: DefaultComponentVariantType | null,
    isPriceBoostActive?: boolean
): React.ReactNode => {
    if (selection.resultType === 'void') {
        return <NotRunningPriceView variant={variant} />;
    }

    if (!selection.display) {
        return <NotVisiblePriceView />;
    }

    return (
        <SelectionRaceCard
            selectionId={selection.id2}
            sp={false}
            borderLeft={true}
            borderRight={true}
            isRacingPage={true}
            isPriceBoostActive={isPriceBoostActive}
        />
    );
};

export const getBoostedPrice = (
    selection: SelectionModel,
    variant: DefaultComponentVariantType | null
): React.ReactNode => {
    if (selection.resultType === 'void') {
        return <NotRunningPriceView variant={variant} />;
    }

    if (!selection.display) {
        return <NotVisiblePriceView />;
    }

    return (
        <SelectionRaceCard
            selectionId={selection.id2}
            sp={false}
            borderLeft={true}
            isRacingPage={true}
            useBoostedPrice={true}
        />
    );
};

export const getSP = (selection: SelectionModel, variant: DefaultComponentVariantType | null): React.ReactNode => {
    if (selection.resultType === 'void') {
        return <NotRunningPriceView variant={variant} />;
    }

    if (!selection.display) {
        return <NotVisiblePriceView />;
    }

    return (
        <SelectionRaceCard
            selectionId={selection.id2}
            sp={true}
            borderLeft={true}
            isRacingPage={true}
        />
    );
};

const getCast = (
    selection: SelectionModel,
    idx: number,
    variant: DefaultComponentVariantType | null
): React.ReactNode => {
    if (selection.resultType === 'void') {
        return <NotRunningPriceView variant={variant} />;
    }

    if (!selection.display) {
        return <NotVisiblePriceView />;
    }

    return (
        <SelectionCastByPlace
            selectionId={selection.id2}
            index={idx}
        />
    );
};

const getAnyCast = (
    selection: SelectionModel,
    idx: number,
    variant: DefaultComponentVariantType | null
): React.ReactNode => {
    if (selection.resultType === 'void') {
        return <NotRunningPriceView variant={variant} />;
    }

    if (!selection.display) {
        return <NotVisiblePriceView />;
    }

    return (
        <SelectionCast
            selectionId={selection.id2}
            title={
                <I18n
                    langKey='events.race-summary.any.label'
                    defaultText='Any'
                />
            }
            index={idx}
            borderLeft={true}
        />
    );
};

interface TcFcType {
    oneCast: React.ReactNode;
    twoCast: React.ReactNode;
    threeCast?: React.ReactNode;
    anyCast: React.ReactNode;
}

export const getTcFc = (
    selection: SelectionModel,
    market: MarketModel,
    variant: DefaultComponentVariantType | null
): TcFcType => {
    if (!market.tricastsOffered) {
        return {
            oneCast: getCast(selection, 0, variant),
            twoCast: getCast(selection, 1, variant),
            anyCast: getAnyCast(selection, 2, variant),
        };
    }

    return {
        oneCast: getCast(selection, 0, variant),
        twoCast: getCast(selection, 1, variant),
        threeCast: getCast(selection, 2, variant),
        anyCast: getAnyCast(selection, 3, variant),
    };
};
